import React, { useMemo, useRef, useState } from "react";

export default function useStateOrBind<T, Context = undefined>(
  initialState: T | (() => T),
  [value, onChange]: [T, (value: T, context?: Context) => void]
) {
  const [state, setState] = useState(initialState);
  const valueRef = useRef(value);
  const internalStateRef = useRef(value);

  valueRef.current = value;
  internalStateRef.current = state;

  const createSetState = (hasValue: boolean) => (
    newState: React.SetStateAction<T>,
    context?: Context
  ) => {
    const actioned =
      typeof newState === "function"
        ? (newState as (value: T) => T)(
            (hasValue ? valueRef : internalStateRef).current
          )
        : newState;

    onChange?.(actioned, context);

    setState(actioned);
  };

  return [
    value === undefined ? state : value,
    createSetState(value !== undefined),
  ] as const;
}

useStateOrBind.provideImmediately = <T extends object>(
  initialState: T | (() => T),
  [value, onChange]: [T, (value: T) => void]
): useState.Result<T> => {
  const [state, setState] = useState(initialState);
  const valueRef = useRef(value);
  const staleRefs = useMemo(() => new WeakSet(), []);

  if (!staleRefs.has(value)) {
    valueRef.current = value;
    staleRefs.add(value);
  }

  const createSetState = (hasValue: boolean) => (
    newState: React.SetStateAction<T>
  ) => {
    setState((currentState) => {
      const actioned =
        typeof newState === "function"
          ? (newState as (value: T) => T)(
              hasValue ? valueRef.current : currentState
            )
          : newState;

      onChange?.(actioned);

      staleRefs.add(valueRef.current);
      valueRef.current = actioned;

      return actioned;
    });
  };

  return [
    value === undefined ? state : valueRef.current,
    createSetState(value !== undefined),
  ];
};
